import React from 'react';
import type { Contract } from '../../types/contracts';

interface ContractVersionsProps {
  contract: Contract;
}

const ContractVersions: React.FC<ContractVersionsProps> = ({ contract }) => {
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Document Versions</h3>
          <p className="text-sm text-gray-600 mt-1">Track changes and maintain version history</p>
        </div>
        <button
          onClick={() => {
            /* TODO: Implement upload version modal */
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Upload Version
        </button>
      </div>

      {contract.versions.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No versions uploaded</h3>
          <p className="text-gray-500 mb-4">Upload the initial contract document to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contract.versions
            .sort((a, b) => b.versionNumber - a.versionNumber)
            .map(version => (
              <div
                key={version.versionNumber}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        v{version.versionNumber}
                      </span>
                      {version.versionNumber === contract.currentVersion && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Current
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-900">{version.fileName}</span>
                    </div>

                    <p className="text-sm text-gray-600 mt-1">{version.changeLog}</p>

                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>Uploaded {new Date(version.uploadedAt).toLocaleDateString()}</span>
                      <span>{(version.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => window.open(version.fileUrl, '_blank')}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Download
                    </button>
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ContractVersions;
